import { getEnv } from '../config';
const MOCK_ACCESS_TOKEN_VALUE = 'mock-access-token';
/**
 * CLIENT_ID/APS_ENV are runtime values injected via /environment.json at deploy
 * time (see .cloudos/web/service_config/*), not build-time env vars — a single
 * bundle is deployed to every environment. `initAuthConfig` must run after
 * `loadEnvironment()` resolves (see index.tsx) and before any auth calls,
 * otherwise CLIENT_ID stays empty and the app silently falls back to mock mode.
 */
let CLIENT_ID = process.env.REACT_APP_AUTH_CLIENT_ID ?? '';
/** `dev` | `stg` | `prod` — selects APS authorize host (pim-db-migration-admin). */
let APS_ENV = (process.env.REACT_APP_APS_ENVIRONMENT ?? 'stg').toLowerCase();
/** Backend host for /auth/token and /auth/refresh — these only exist behind the Apigee proxy, not on the frontend's own origin. */
let API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';
let MOCK_MODE = !CLIENT_ID;
export function initAuthConfig() {
    CLIENT_ID = getEnv('AUTH_CLIENT_ID') || process.env.REACT_APP_AUTH_CLIENT_ID || '';
    APS_ENV = (getEnv('APS_ENVIRONMENT') || process.env.REACT_APP_APS_ENVIRONMENT || 'stg').toLowerCase();
    API_BASE_URL = getEnv('API_BASE_URL') || process.env.REACT_APP_API_BASE_URL || '';
    MOCK_MODE = !CLIENT_ID;
    scheduleStoredRefresh();
}
/** Must exactly match the callback URL registered on the APS app (origin root, no path). */
const REDIRECT_URI = `${window.location.origin}/`;
const LS_ACCESS = 'accessToken';
const LS_REFRESH = 'auth_refresh_token';
const LS_EXPIRES = 'auth_token_expires_at';
/** Shared by scheduleRefresh and ensureFreshToken — they must agree on this window or the mount-time race it closes can reopen. */
const REFRESH_LEAD_MS = 5 * 60 * 1000;
const SS_VERIFIER = 'pkce_verifier';
const SS_STATE = 'oauth_state';
const APS_AUTH_BASE_URLS = {
    dev: 'https://developer-dev.api.autodesk.com/authentication/v2',
    stg: 'https://developer-stg.api.autodesk.com/authentication/v2',
    prod: 'https://developer.api.autodesk.com/authentication/v2',
};
function apsAuthorizeBase() {
    const key = APS_ENV === 'dev' || APS_ENV === 'stg' || APS_ENV === 'prod' ? APS_ENV : 'prod';
    return APS_AUTH_BASE_URLS[key] ?? APS_AUTH_BASE_URLS.prod;
}
function randomUrlSafe(bytes) {
    const array = new Uint8Array(bytes);
    crypto.getRandomValues(array);
    return base64UrlEncode(array.buffer);
}
/** Used when OAuth is disabled (mock mode) and `/v1/auth/me` is unavailable (API not running). */
export const MOCK_DEV_USER = {
    userId: 'dev-user-123',
    email: 'developer@autodesk.com',
    name: 'Developer',
    role: 'admin',
    actions: ['READ', 'WRITE', 'DELETE'],
};
function base64UrlEncode(buffer) {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}
async function sha256(plain) {
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(plain));
}
function generateCodeVerifier() {
    return randomUrlSafe(32);
}
export const AuthClient = {
    isMockMode: () => MOCK_MODE,
    async startLogin() {
        if (MOCK_MODE) {
            localStorage.setItem(LS_ACCESS, MOCK_ACCESS_TOKEN_VALUE);
            window.location.href = '/';
            return;
        }
        const verifier = generateCodeVerifier();
        const state = randomUrlSafe(16);
        sessionStorage.setItem(SS_VERIFIER, verifier);
        sessionStorage.setItem(SS_STATE, state);
        const challenge = base64UrlEncode(await sha256(verifier));
        const params = new URLSearchParams({
            response_type: 'code',
            client_id: CLIENT_ID,
            redirect_uri: REDIRECT_URI,
            scope: 'openid data:read data:write',
            state,
            code_challenge: challenge,
            code_challenge_method: 'S256',
        });
        window.location.href = `${apsAuthorizeBase()}/authorize?${params}`;
    },
    async handleCallback(code, stateFromUrl) {
        const expectedState = sessionStorage.getItem(SS_STATE);
        sessionStorage.removeItem(SS_STATE);
        if (!stateFromUrl || !expectedState || stateFromUrl !== expectedState) {
            throw new Error('oauth_state_mismatch');
        }
        const codeVerifier = sessionStorage.getItem(SS_VERIFIER) ?? '';
        sessionStorage.removeItem(SS_VERIFIER);
        const res = await fetch(`${API_BASE_URL}/v1/auth/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, codeVerifier, redirectUri: REDIRECT_URI }),
        });
        if (!res.ok)
            throw new Error('Token exchange failed');
        const { accessToken, refreshToken, expiresAt } = (await res.json());
        localStorage.setItem(LS_ACCESS, accessToken);
        localStorage.setItem(LS_REFRESH, refreshToken);
        localStorage.setItem(LS_EXPIRES, String(expiresAt));
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('token_expires_at');
        scheduleRefresh(expiresAt);
    },
    refreshToken() {
        if (refreshInFlight)
            return refreshInFlight;
        refreshInFlight = doRefresh().finally(() => {
            refreshInFlight = null;
        });
        return refreshInFlight;
    },
    /**
     * Awaits an in-progress or newly-triggered refresh if the token is inside (or past) the
     * same 5-minute lead `scheduleRefresh` uses, so callers that fire right on mount (e.g.
     * AuthProvider's `/v1/auth/me`) don't race a stale token against the background refresh
     * timer and get bounced through a full sign-out by the response interceptor's 401 handler.
     */
    async ensureFreshToken() {
        if (MOCK_MODE)
            return;
        const storedExpiry = localStorage.getItem(LS_EXPIRES) ?? localStorage.getItem('token_expires_at');
        if (!storedExpiry)
            return;
        const expiresAt = parseInt(storedExpiry, 10);
        if (expiresAt - Date.now() < REFRESH_LEAD_MS) {
            await AuthClient.refreshToken();
        }
    },
    logout() {
        localStorage.removeItem(LS_ACCESS);
        localStorage.removeItem(LS_REFRESH);
        localStorage.removeItem(LS_EXPIRES);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('token_expires_at');
        sessionStorage.clear();
        if (refreshTimer !== null) {
            clearTimeout(refreshTimer);
            refreshTimer = null;
        }
        window.location.href = MOCK_MODE
            ? '/'
            : `${apsAuthorizeBase()}/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
    },
    getAccessToken() {
        return localStorage.getItem(LS_ACCESS) ?? localStorage.getItem('access_token');
    },
};
let refreshTimer = null;
let refreshInFlight = null;
async function doRefresh() {
    const refreshToken = localStorage.getItem(LS_REFRESH) ?? localStorage.getItem('refresh_token');
    if (!refreshToken)
        throw new Error('No refresh token');
    const res = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) {
        throw new Error('Refresh failed');
    }
    const { accessToken, refreshToken: newRefresh, expiresAt } = (await res.json());
    localStorage.setItem(LS_ACCESS, accessToken);
    localStorage.setItem(LS_REFRESH, newRefresh);
    localStorage.setItem(LS_EXPIRES, String(expiresAt));
    scheduleRefresh(expiresAt);
}
// The background timer is the only caller with nothing else watching its result — a failed
// refresh here must force a logout itself, unlike `ensureFreshToken` (wants a silent failure,
// the mount-time /v1/auth/me call will surface it) or the response interceptor (already logs
// out on a failed refresh-and-retry).
function refreshOrLogout() {
    void AuthClient.refreshToken().catch(() => AuthClient.logout());
}
function scheduleRefresh(expiresAt) {
    if (refreshTimer !== null)
        clearTimeout(refreshTimer);
    const delay = expiresAt - Date.now() - REFRESH_LEAD_MS;
    if (delay <= 0) {
        refreshOrLogout();
        return;
    }
    refreshTimer = setTimeout(refreshOrLogout, delay);
}
function scheduleStoredRefresh() {
    if (MOCK_MODE)
        return;
    const storedExpiry = localStorage.getItem(LS_EXPIRES) ?? localStorage.getItem('token_expires_at');
    if (storedExpiry)
        scheduleRefresh(parseInt(storedExpiry, 10));
}
